import { useMemo } from 'react';
import { Card, Icon } from '../../ui/index.js';
import { EncryptedImage } from '../viewer/EncryptedImage.js';
import { cn } from '../../lib/cn.js';
import type { DuplicateCluster } from './scan.js';

interface Props {
  cluster: DuplicateCluster;
  albumKey: CryptoKey;
  // photoId → presigned URL. Streamed in by the parent; tiles render
  // 'awaiting-url' until their entry lands.
  presigned: Map<string, string>;
  // Album path lookup (id → ['root', 'sub', 'leaf']) for breadcrumbs.
  albumPathFor: (albumId: string) => string[];
  // Selection: which photoIds are flagged for deletion.
  selectedIds: Set<string>;
  onToggle: (photoId: string) => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function DuplicateClusterCard({
  cluster,
  albumKey,
  presigned,
  albumPathFor,
  selectedIds,
  onToggle,
}: Props) {
  const photos = cluster.photos;
  const isUnverified = cluster.kind === 'unverified';

  // Sort copies by createdAt asc so the "kept" suggestion is always the
  // first tile — gives the user a stable mental model.
  const sorted = useMemo(
    () => [...photos].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [photos],
  );

  return (
    <Card
      className={cn(
        'flex flex-col gap-4',
        isUnverified ? 'border-on-surface-variant/40 border-dashed' : 'border-cyan/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon
              name={isUnverified ? 'help' : 'content_copy'}
              className={cn('text-2xl', isUnverified ? 'text-on-surface-variant' : 'text-cyan')}
            />
            <h3 className="font-display text-base font-semibold">
              {photos.length} copie · {formatBytes(cluster.size)}
            </h3>
          </div>
          <p className="text-xs text-on-surface-variant">
            {isUnverified
              ? 'Stessa dimensione esatta. Contenuto non verificato (file grandi).'
              : 'Contenuto byte-identico (SHA-256).'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {sorted.map((photo, idx) => {
          const url = presigned.get(photo.photoId) ?? null;
          const selected = selectedIds.has(photo.photoId);
          const suggested = idx === 0; // oldest copy = suggested keep
          return (
            <button
              key={photo.photoId}
              type="button"
              onClick={() => onToggle(photo.photoId)}
              className={cn(
                'group relative flex flex-col gap-1 overflow-hidden rounded-xl border-2 bg-surface-container-low p-1 text-left transition-colors',
                selected
                  ? 'border-error'
                  : suggested
                    ? 'border-cyan/60'
                    : 'border-transparent hover:border-on-surface-variant/40',
              )}
            >
              <div className="relative aspect-square w-full overflow-hidden rounded-lg">
                <EncryptedImage
                  id={photo.photoId}
                  url={url}
                  albumKey={albumKey}
                  alt={photo.filename}
                  className="h-full w-full object-cover"
                  {...(photo.mimeType ? { mimeType: photo.mimeType } : {})}
                />
                <div
                  className={cn(
                    'absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full border text-xs',
                    selected
                      ? 'border-error bg-error text-white'
                      : 'border-on-surface-variant/60 bg-surface/80 text-on-surface-variant',
                  )}
                >
                  {selected ? <Icon name="delete" className="text-sm" /> : null}
                </div>
                {suggested && !selected ? (
                  <div className="absolute left-1 top-1 rounded-full border border-cyan/60 bg-surface/80 px-2 py-0.5 text-[10px] font-semibold uppercase text-cyan">
                    Tieni
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col gap-0.5 px-1 pb-1">
                <span
                  className="truncate text-xs font-semibold text-on-surface"
                  title={photo.filename}
                >
                  {photo.filename}
                </span>
                <span
                  className="truncate text-[10px] text-on-surface-variant"
                  title={albumPathFor(photo.albumId).join(' / ')}
                >
                  {albumPathFor(photo.albumId).join(' / ') || '—'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
